import type React from 'react';
import { memo, useCallback, useEffect, useState } from 'react';

import type { GenericDetails } from 'components/Input/Autocomplete';
import { DISPLAY_OPTIONS } from 'components/Toolbar/components/Fields/SearchFilter/types';
import { useDebouncedFilterInput } from 'components/Toolbar/hooks/useDebouncedFilterInput';
import { useFilterProvider } from 'components/Toolbar/hooks/useFilterProvider';
import type { PROVIDER_NAMES } from 'components/Toolbar/hooks/useFilterProvider/types';
import type { FilterProviderResponse } from 'components/Toolbar/hooks/useFilterProvider/useFilterProvider';
import type { Field } from 'components/Toolbar/types';
import { normalizeFieldOptions } from 'components/Toolbar/utils';

import CheckIcon from '@mui/icons-material/Check';
import type { AutocompleteProps } from '@mui/material';
import { Autocomplete, Box, Grid2, Popper, TextField, useTheme } from '@mui/material';

import { DropdownBox } from '../DropdownBox';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';

export const MAX_SEARCH_FIELD_WIDTH = '344px';

const getFieldStyles = (displayOption?: DISPLAY_OPTIONS) => ({
    width: '100%',
    minWidth: '200px',
    maxWidth: displayOption === DISPLAY_OPTIONS.FORM_FIELD ? MAX_SEARCH_FIELD_WIDTH : '100%',
    p: displayOption !== DISPLAY_OPTIONS.FORM_FIELD ? 2 : undefined,
});

const getOptionLabel = (option: GenericDetails | string) => (typeof option === 'string' ? option : option.name);

const isOptionEqualToValue = (option: GenericDetails, value: GenericDetails) => option.id === value.id;

interface SearchFilterProps<T extends GenericDetails>
    extends Omit<AutocompleteProps<T, boolean | undefined, boolean | undefined, boolean | undefined>, 'inputValue' | 'renderInput'> {
    options: T[];
    selectedItem?: T | null;
    setSelectedItem: (item: T | null) => void;
    onClear?: () => void;
    handleClose?: () => void;
    providerName?: PROVIDER_NAMES;
    additionalParams?: Record<string, any>;
    name?: string;
    label?: string;
    placeholder?: string;
    optionName: string;
    optionValue: string;
    optionId: string;
    displayOption?: DISPLAY_OPTIONS;
    autoFocus?: boolean;
}

const SearchFilter = memo(
    <T extends GenericDetails>({
        selectedItem,
        options = [],
        setSelectedItem,
        onClear,
        handleClose,
        placeholder,
        providerName,
        name,
        additionalParams,
        optionName,
        optionValue,
        optionId,
        label,
        displayOption,
        autoFocus = true,
        ...rest
    }: SearchFilterProps<T>) => {
        const theme = useTheme();
        const [open, setOpen] = useState(false);
        const isFormField = displayOption === DISPLAY_OPTIONS.FORM_FIELD;

        const { sortedOptions, localInputValue, handleApply, handleChange, debouncedInputChange, setOptionsList, maxItems, isFieldEmpty } =
            useDebouncedFilterInput({
                options,
                selectedItem,
                setSelectedItem,
                onClear,
                handleClose,
            });

        const { data, isLoading, isFetched, isFetching } = useFilterProvider<T>(providerName, localInputValue, {
            additionalParams,
            select: (raw) => {
                if (typeof raw === 'object' && 'items' in raw && raw.items !== undefined) {
                    return raw.items as FilterProviderResponse<T>;
                }
                return raw;
            },
        });

        useEffect(() => {
            if (data) {
                setOptionsList(normalizeFieldOptions(data as unknown as Field['options'], optionName, optionId, optionValue) as T[]);
            }
        }, [data, optionName, optionId, optionValue]);

        const handleCancel = useCallback(() => {
            if (handleClose) {
                handleClose();
            }
        }, [handleClose]);

        return (
            <Autocomplete
                open={open}
                onOpen={() => {
                    setOpen(true);
                }}
                onClose={(event, reason) => {
                    if (reason !== 'blur') {
                        setOpen(false);
                    }
                }}
                freeSolo
                data-testid={`${name}_search_filter`}
                data-locator={`${name}_search_filter`}
                sx={getFieldStyles(displayOption)}
                getOptionLabel={getOptionLabel}
                options={sortedOptions}
                autoComplete
                includeInputInList
                value={selectedItem ?? null}
                blurOnSelect={isFormField}
                isOptionEqualToValue={isOptionEqualToValue}
                noOptionsText="No results"
                onChange={
                    handleChange as (_event: React.SyntheticEvent, newItem: T | NonNullable<string | T> | (string | T)[] | null) => void
                }
                onInputChange={(_event, newInputValue) => {
                    if (newInputValue) debouncedInputChange(newInputValue);
                }}
                forcePopupIcon={false}
                disableClearable={!isFormField}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        // eslint-disable-next-line jsx-a11y/no-autofocus
                        autoFocus={autoFocus}
                        label={label}
                        placeholder={placeholder}
                        fullWidth
                        sx={{
                            '& .MuiInputBase-root': {
                                height: '45px',
                                py: 0,
                                '& .MuiOutlinedInput-root': {
                                    pt: '2px',
                                    pl: '9px',
                                },
                            },
                        }}
                    />
                )}
                disablePortal
                renderOption={(props, option, { inputValue, selected }) => {
                    const matches = match(option.name, inputValue, { insideWords: true });
                    const parts = parse(option.name, matches);

                    return (
                        <li {...props}>
                            <Grid2 container alignItems="center" justifyContent="space-between">
                                <Box
                                    sx={{
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        display: '-webkit-box',
                                        WebkitBoxOrient: 'vertical',
                                        WebkitLineClamp: 4,
                                        whiteSpace: 'pre-line',
                                    }}
                                >
                                    {parts.map((part, index) => (
                                        <span
                                            key={`${part.text}-${index}`}
                                            style={{
                                                fontWeight: part.highlight ? 700 : 400,
                                                color: part.highlight ? theme.palette.common.black : 'black',
                                            }}
                                        >
                                            {part.text}
                                        </span>
                                    ))}
                                </Box>
                                {selected && (
                                    <Grid2 display="flex" alignSelf="flex-end" justifyItems="center">
                                        <CheckIcon color="primary" />
                                    </Grid2>
                                )}
                            </Grid2>
                        </li>
                    );
                }}
                {...rest}
                slots={{
                    popper: (props) => {
                        const dropdownContent = (
                            <DropdownBox
                                {...props}
                                maxItems={maxItems}
                                style={{ width: isFormField ? MAX_SEARCH_FIELD_WIDTH : 'unset' }}
                                withResults
                                isLoading={isLoading}
                                isFetched={isFetched}
                                isFetching={isFetching}
                                isFieldEmpty={isFieldEmpty}
                                onCancel={handleCancel}
                                onApply={handleApply}
                                displayAsField={isFormField}
                                withActionButtons
                            />
                        );
                        return props.disablePortal === false ? (
                            <Popper
                                {...props}
                                disablePortal={false}
                                placement="bottom-start"
                                sx={(() => {
                                    const sxProp = props.sx;
                                    const sxObject = typeof sxProp === 'object' && sxProp !== null && !Array.isArray(sxProp) ? sxProp : {};
                                    return Object.assign({ zIndex: theme.zIndex.modal }, sxObject as Record<string, unknown>);
                                })()}
                            >
                                {dropdownContent}
                            </Popper>
                        ) : (
                            dropdownContent
                        );
                    },
                }}
            />
        );
    }
);

SearchFilter.displayName = 'SearchFilter';

export { SearchFilter };
